import { colorsV2 } from '@doltech/ui/lib/figma/colors-v2';
import { DragIn } from '@doltech/ui/lib/figma/DesignSystemV2/DragIn';
import {
  DragOutMobile,
  DragOutMobileResult,
} from '@doltech/ui/lib/figma/DesignSystemV2/DragOutMobile';
import { MarkedTypeEnum, TestModeEnum } from '@doltech/utils/lib/constants';
import { isNumber } from '@doltech/utils/lib/number';
import { numberToABC } from '@doltech/utils/lib/text';
import cl from 'classnames';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import styled from 'styled-components';

import { ExtraQuestionProps } from '../../components/questions/BaseQuestion';
import { ResultDragInQuestionInstance } from '../../model/Question/result-drag-in-question.store';
import { useTestResultStore } from '../../model/test-result.hook';

export const MOBILE_BOX_PADDING = 16;

interface DragInResultMobileProps extends React.HTMLAttributes<HTMLDivElement> {
  data: ResultDragInQuestionInstance & ExtraQuestionProps;
  isMobileView?: boolean;
}

export const scaleDraggingPoint = (originalImageSize, scaledImageSize, originalPointCoordinate) => {
  const percentWidth = (scaledImageSize * 100) / originalImageSize;
  return (percentWidth * originalPointCoordinate) / 100;
};

export const DragResultOptions = (props) => {
  const { results, testMode } = props;

  return (
    <ResultsMobile>
      {results?.map(
        ({
          number,
          id,
          userAnswer,
          markedType,
          correctText,
          correctAnswer,
          isWatching,
          userText,
          goHere,
        }) =>
          isWatching ? (
            <DragOutMobileResult
              onClick={goHere}
              key={id}
              number={number}
              type={markedType}
              correctAnswer={correctAnswer}
              correctText={correctText}
              userText={userText}
              userAnswer={userAnswer}
              testMode={testMode}
            />
          ) : (
            <DragOutMobile
              onClick={goHere}
              number={number}
              className={cl('no-focus', { empty: userAnswer === undefined })}
              answer={userAnswer}
              text={correctText}
              key={id}
            />
          )
      )}
    </ResultsMobile>
  );
};

export const DragInQuestionResultMobile = observer((props: DragInResultMobileProps) => {
  const store = useTestResultStore();
  const { data, isMobileView, ...rest } = props;
  const { options, image, no, index, userAnswers, marked, correctOptions, questions, scroll } =
    data;
  const [scaledImageWidth, setScaledImageWidth] = React.useState(undefined);
  const [scaledImageHeight, setScaledImageHeight] = React.useState(undefined);

  let defaultMarkedType = null;
  if (store.testMode === TestModeEnum.EXPLANATION) {
    defaultMarkedType = MarkedTypeEnum.CORRECT;
  }

  const dragInResults = React.useMemo(
    () =>
      questions.map((question, idx) => {
        const markedType = defaultMarkedType || marked[idx];
        const userAnswerKey = userAnswers[idx];
        const optionIndex = correctOptions[idx].key;
        const number = no + idx - index;

        return {
          ...question,
          number,
          correctText: options[optionIndex],
          correctAnswer: numberToABC(optionIndex + 1),
          userAnswer: isNumber(userAnswerKey) ? numberToABC(userAnswerKey + 1) : undefined,
          userText: isNumber(userAnswerKey) ? options[userAnswerKey] : undefined,
          markedType,
          isWatching: index === idx,
          goHere: () => {
            store.goToQuestion(number, scroll);
          },
        };
      }),
    [
      questions,
      defaultMarkedType,
      marked,
      userAnswers,
      correctOptions,
      no,
      index,
      options,
      store,
      scroll,
    ]
  );

  const imgElement = React.useRef(null);

  return (
    <MainMobile className="drag-in-answer dol-question" {...rest}>
      <ImageContainer>
        <Img
          loading="lazy"
          alt="Drag in result image"
          src={image.url}
          className="diagram-img"
          ref={imgElement}
          onLoad={() => {
            setScaledImageWidth(imgElement.current.clientWidth);
            setScaledImageHeight(imgElement.current.clientHeight);
          }}
        />
      </ImageContainer>
      {dragInResults.map(
        ({ number, x, y, id, userAnswer, isWatching, markedType, goHere, correctAnswer }) => (
          <Spot
            className="single-spot"
            style={{
              left: scaleDraggingPoint(image.width, scaledImageWidth, x) + MOBILE_BOX_PADDING,
              top: scaleDraggingPoint(image.height, scaledImageHeight, y) + MOBILE_BOX_PADDING,
            }}
            key={id}
          >
            <DragIn
              onClick={goHere}
              number={number}
              className={cl({
                empty: store.testMode !== TestModeEnum.EXPLANATION && userAnswer === undefined,
                'no-focus': !isWatching,
                'in-correct': markedType === MarkedTypeEnum.WRONG,
                missed: markedType === MarkedTypeEnum.MISSING,
                correct: markedType === MarkedTypeEnum.CORRECT,
              })}
              answer={store.testMode === TestModeEnum.EXPLANATION ? correctAnswer : userAnswer}
            />
          </Spot>
        )
      )}
      <DragResultOptions testMode={store.testMode} results={dragInResults} />
    </MainMobile>
  );
});

export const MainMobile = styled.div`
  box-sizing: border-box;
  box-shadow: 0 2px 2px ${colorsV2.blackShadow4};
  border-radius: 16px;
  position: relative;
`;

export const ImageContainer = styled.div`
  background-color: ${colorsV2.white100};
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.04), 0 0 2px rgba(0, 0, 0, 0.06),
    0 0 1px rgba(0, 0, 0, 0.04);
  padding: ${MOBILE_BOX_PADDING}px;

  .diagram-img {
    width: 100%;
    height: auto;
  }
`;

export const ResultsMobile = styled.div`
  background-color: ${colorsV2.white100};
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.04), 0 0 2px rgba(0, 0, 0, 0.06),
    0 0 1px rgba(0, 0, 0, 0.04);
  margin-top: 8px;
  padding: ${MOBILE_BOX_PADDING}px;

  > span {
    margin: 0;

    &:not(:last-child) {
      margin-bottom: 8px;
    }
  }
  .single-spot {
    > span {
      padding: 5px 6px;
    }
  }
`;

export const Spot = styled.span`
  position: absolute;
`;

export const Img = styled.img``;
